import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";

const isApp = () => {
  try {
    return (
      window.location.protocol === "capacitor:" ||
      window.navigator.userAgent.includes("CapacitorWebView") ||
      (typeof window.Capacitor !== "undefined" &&
       window.Capacitor.isNativePlatform &&
       window.Capacitor.isNativePlatform() === true) ||
      document.URL.startsWith("capacitor://")
    );
  } catch {
    return false;
  }
};

const SLIDES = [
  {
    emoji: "📱",
    color: "linear-gradient(135deg, #D41A6E 0%, #E87010 100%)",
    title: "Scanne",
    subtitle: "Tu vois un produit qui t'intéresse ?",
    desc: "Pointe ton téléphone sur le code-barres. En 2 secondes c'est dans ta liste."
  },
  {
    emoji: "💾",
    color: "linear-gradient(135deg, #B01558 0%, #D41A6E 100%)",
    title: "Sauvegarde",
    subtitle: "Il rejoint ta Crush List.",
    desc: "Tous tes produits coup de cœur au même endroit. Retrouve-les quand tu veux."
  },
  {
    emoji: "🔔",
    color: "linear-gradient(135deg, #E83A80 0%, #F08030 100%)",
    title: "Reste informé",
    subtitle: "Le vendeur met à jour ?",
    desc: "Prix, stock, promo — tu es le premier averti. Zéro effort."
  }
];

const BTN_PRIMARY = {
  width: "100%", maxWidth: "320px", height: "52px",
  background: "white", color: "#D41A6E",
  border: "none", borderRadius: "14px",
  fontSize: "16px", fontWeight: 800, cursor: "pointer",
  boxShadow: "0 4px 20px rgba(0,0,0,0.15)"
};

const BTN_SECONDARY = {
  width: "100%", maxWidth: "320px", height: "52px",
  background: "rgba(255,255,255,0.15)", color: "white",
  border: "2px solid rgba(255,255,255,0.5)", borderRadius: "14px",
  fontSize: "16px", fontWeight: 700, cursor: "pointer"
};

const LandingApp = ({ navigate }) => {
  const [current, setCurrent] = useState(0);
  const slide = SLIDES[current];
  const isLast = current === SLIDES.length - 1;

  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e) => setTouchStart(e.targetTouches[0].clientX);
  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > minSwipeDistance && current < SLIDES.length - 1) setCurrent(current + 1);
    if (distance < -minSwipeDistance && current > 0) setCurrent(current - 1);
    setTouchStart(null);
    setTouchEnd(null);
  };

  return (
    <div
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{
        height: "100vh", display: "flex", flexDirection: "column",
        background: slide.color, transition: "background 0.4s ease",
        fontFamily: "'Inter', system-ui, sans-serif",
        overflow: "hidden", position: "relative"
      }}>
      {!isLast && (
        <button
          onClick={() => setCurrent(SLIDES.length - 1)}
          style={{
            position: "absolute", top: "16px", right: "16px",
            background: "rgba(255,255,255,0.2)", border: "none",
            color: "white", fontSize: "13px", fontWeight: 600,
            padding: "6px 14px", borderRadius: "999px", cursor: "pointer", zIndex: 10
          }}
        >
          Passer →
        </button>
      )}

      <div style={{
        flex: 1, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        padding: "40px 32px", textAlign: "center", gap: "20px"
      }}>
        <img
          src="/logo_crushnow.jpeg"
          alt="CrushNow"
          style={{ width: "72px", height: "72px", borderRadius: "18px", marginBottom: "4px", boxShadow: "0 4px 16px rgba(0,0,0,0.2)" }}
        />
        <div style={{ fontSize: "72px", lineHeight: 1 }}>{slide.emoji}</div>
        <h1 style={{ fontSize: "42px", fontWeight: 900, color: "white", margin: 0, letterSpacing: "-1px" }}>
          {slide.title}
        </h1>
        <p style={{ fontSize: "18px", fontWeight: 600, color: "rgba(255,255,255,0.9)", margin: 0 }}>
          {slide.subtitle}
        </p>
        <p style={{ fontSize: "15px", color: "rgba(255,255,255,0.75)", margin: 0, maxWidth: "280px", lineHeight: 1.6 }}>
          {slide.desc}
        </p>
      </div>

      <div style={{ padding: "0 32px 48px", display: "flex", flexDirection: "column", gap: "16px", alignItems: "center" }}>
        <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
          {SLIDES.map((_, i) => (
            <div key={i} onClick={() => setCurrent(i)} style={{
              width: i === current ? "24px" : "8px", height: "8px",
              borderRadius: "999px",
              background: i === current ? "white" : "rgba(255,255,255,0.4)",
              cursor: "pointer", transition: "all 0.3s ease"
            }} />
          ))}
        </div>

        {isLast ? (
          <>
            <button onClick={() => navigate("/login")} style={BTN_PRIMARY}>
              Se connecter
            </button>
            <button onClick={() => navigate("/register")} style={BTN_SECONDARY}>
              Créer un compte
            </button>
            <p style={{ fontSize: "11px", color: "rgba(255,255,255,0.5)", margin: 0 }}>
              CrushNow® · v1.0.4
            </p>
          </>
        ) : (
          <button onClick={() => setCurrent(current + 1)} style={BTN_SECONDARY}>
            Suivant →
          </button>
        )}
      </div>
    </div>
  );
};

const LandingWeb = ({ navigate }) => {
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.15 }
    );
    document.querySelectorAll(".land-section").forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const scrollToVendeurs = () => {
    const el = document.getElementById("vendeurs");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div style={{ fontFamily: "'Inter', system-ui, sans-serif", background: "#0A0A0F", width: "100%" }}>
      <style>{`
        .land-section { opacity: 0; transform: translateY(30px); transition: opacity 0.7s ease, transform 0.7s ease; }
        .land-section.visible { opacity: 1; transform: translateY(0); }
      `}</style>

      {/* ── HERO split 50/50 ── */}
      <div style={{ display: "flex", alignItems: "stretch", minHeight: "100vh", flexWrap: "wrap" }}>
        {/* Gauche texte */}
        <div style={{ flex: "1 1 340px", background: "#0A0A0F", padding: "80px 48px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "rgba(212,26,110,0.15)", borderRadius: "999px", padding: "5px 14px", marginBottom: "28px", border: "1px solid rgba(212,26,110,0.3)", width: "fit-content" }}>
            <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#D41A6E", display: "inline-block" }} />
            <span style={{ fontSize: "11px", color: "#E87010", fontWeight: 700, letterSpacing: "1px" }}>DISPONIBLE SUR GOOGLE PLAY</span>
          </div>
          <h1 style={{ fontSize: "clamp(32px, 4vw, 52px)", fontWeight: 900, color: "white", margin: "0 0 20px", lineHeight: 1.1, letterSpacing: "-2px" }}>
            Tu l'as vu.<br />Tu l'as aimé.<br /><span style={{ color: "#D41A6E" }}>Tu ne l'oublieras plus.</span>
          </h1>
          <p style={{ fontSize: "17px", color: "rgba(255,255,255,0.55)", margin: "0 0 36px", lineHeight: 1.7, maxWidth: "380px" }}>
            Scanne un produit en magasin. Retrouve-le. Sois alerté quand il change.
          </p>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "16px" }}>
            <a
              href="https://play.google.com/store/apps/details?id=com.liabra.crushme"
              target="_blank"
              rel="noopener noreferrer"
              style={{ height: "50px", padding: "0 32px", background: "linear-gradient(135deg, #D41A6E, #E87010)", color: "white", border: "none", borderRadius: "12px", fontSize: "15px", fontWeight: 800, cursor: "pointer", display: "inline-flex", alignItems: "center", textDecoration: "none" }}
            >
              Télécharger l'app
            </a>
            <button
              onClick={() => navigate("/register")}
              style={{ height: "50px", padding: "0 32px", background: "transparent", color: "white", border: "1.5px solid rgba(255,255,255,0.2)", borderRadius: "12px", fontSize: "15px", fontWeight: 600, cursor: "pointer" }}
            >
              Je veux crusher →
            </button>
            <button
              onClick={scrollToVendeurs}
              style={{ height: "50px", padding: "0 32px", background: "transparent", color: "white", border: "1.5px solid rgba(255,255,255,0.2)", borderRadius: "12px", fontSize: "15px", fontWeight: 600, cursor: "pointer" }}
            >
              Je suis commerçant →
            </button>
          </div>
          <p
            onClick={() => navigate("/login")}
            style={{ fontSize: "14px", color: "rgba(255,255,255,0.6)", margin: "0 0 28px", cursor: "pointer" }}
          >
            J'ai déjà un compte →
          </p>
        </div>
        {/* Droite image */}
        <div style={{ flex: "1 1 340px", background: "#180818", display: "flex", alignItems: "center", justifyContent: "center", padding: "40px", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 60% 50%, rgba(212,26,110,0.2) 0%, transparent 70%)" }} />
          <img src="/landing1.png" alt="CrushNow app" style={{ width: "100%", maxWidth: "480px", height: "auto", objectFit: "contain", position: "relative", zIndex: 1 }} />
        </div>
      </div>

      {/* ── SECTION CLIENT : Comment ca marche ── */}
      <div className="land-section" style={{ background: "#0F0F1A", padding: "80px 48px" }}>
        <div style={{ textAlign: "center", marginBottom: "56px" }}>
          <span style={{ fontSize: "10px", fontWeight: 700, color: "#D41A6E", letterSpacing: "3px", display: "block", marginBottom: "16px" }}>POUR LES CLIENTS</span>
          <h2 style={{ fontSize: "clamp(28px, 3vw, 40px)", fontWeight: 900, color: "white", margin: 0, letterSpacing: "-1px" }}>
            Comment ça marche
          </h2>
        </div>
        <div style={{ display: "flex", gap: "24px", flexWrap: "wrap", justifyContent: "center" }}>
          {[
            { num: "01", title: "Tu scannes", desc: "Tu scannes le code-barres en magasin avec ton téléphone.", color: "#D41A6E" },
            { num: "02", title: "Tu sauvegardes", desc: "Le produit rejoint ta Crush List. Retrouve-le quand tu veux.", color: "#C8306E" },
            { num: "03", title: "Tu es alerté", desc: "Tu reçois une alerte si le vendeur met à jour prix, stock ou promo.", color: "#E87010" },
          ].map((step, i) => (
            <div key={i} style={{ flex: "1 1 260px", maxWidth: "320px", background: "#13131F", borderRadius: "20px", padding: "36px 28px", border: "1px solid rgba(255,255,255,0.06)", position: "relative", overflow: "hidden" }}>
              <div style={{ fontSize: "72px", fontWeight: 900, color: step.color, opacity: 0.12, position: "absolute", top: "8px", right: "18px", lineHeight: 1, userSelect: "none" }}>{step.num}</div>
              <div style={{ fontSize: "32px", fontWeight: 900, color: step.color, marginBottom: "16px", lineHeight: 1 }}>{step.num}</div>
              <h3 style={{ fontSize: "20px", fontWeight: 800, color: "white", margin: "0 0 12px" }} dangerouslySetInnerHTML={{ __html: step.title }} />
              <p style={{ fontSize: "14px", color: "rgba(255,255,255,0.5)", margin: 0, lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: step.desc }} />
            </div>
          ))}
        </div>
      </div>

      {/* ── SECTION VENDEUR ── */}
      <div id="vendeurs" className="land-section" style={{ background: "#0F0F1A", padding: "80px 48px", borderTop: "1px solid rgba(232,112,16,0.2)" }}>
        <div style={{ maxWidth: "800px", margin: "0 auto", textAlign: "center" }}>
          <span style={{ fontSize: "10px", fontWeight: 700, color: "#E87010", letterSpacing: "3px", display: "block", marginBottom: "16px" }}>POUR LES COMMERÇANTS</span>
          <h2 style={{ fontSize: "clamp(28px, 3vw, 42px)", fontWeight: 900, color: "white", margin: "0 0 18px", letterSpacing: "-1px" }}>
            Vous êtes commerçant ?
          </h2>
          <p style={{ fontSize: "17px", color: "rgba(255,255,255,0.55)", margin: "0 0 56px", lineHeight: 1.7 }}>
            Vos clients vous ont déjà crushés. Rejoignez CrushNow pour les alerter.
          </p>
          <div style={{ display: "flex", gap: "24px", flexWrap: "wrap", justifyContent: "center", marginBottom: "48px" }}>
            {[
              { icon: "📦", title: "Vos produits déjà scannés", desc: "Vos articles sont déjà dans la base. Ils attendent que vous les reclamiez." },
              { icon: "🔔", title: "Alertez vos clients en 1 clic", desc: "Prix, stock, promo — envoyez une notification à tous vos crushers." },
              { icon: "📊", title: "Tableau de bord simple", desc: "Suivez vos produits les plus crushés et mesurez l'engagement." },
            ].map((arg, i) => (
              <div key={i} style={{ flex: "1 1 200px", maxWidth: "230px", background: "rgba(232,112,16,0.07)", borderRadius: "16px", padding: "28px 20px", border: "1px solid rgba(232,112,16,0.15)", textAlign: "left" }}>
                <div style={{ fontSize: "28px", marginBottom: "14px" }}>{arg.icon}</div>
                <h3 style={{ fontSize: "15px", fontWeight: 800, color: "white", margin: "0 0 10px" }} dangerouslySetInnerHTML={{ __html: arg.title }} />
                <p style={{ fontSize: "13px", color: "rgba(255,255,255,0.45)", margin: 0, lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: arg.desc }} />
              </div>
            ))}
          </div>
          <button
            onClick={() => navigate("/register")}
            style={{ height: "54px", padding: "0 40px", background: "linear-gradient(135deg, #E87010, #D41A6E)", color: "white", border: "none", borderRadius: "14px", fontSize: "16px", fontWeight: 800, cursor: "pointer", boxShadow: "0 8px 32px rgba(232,112,16,0.3)" }}
          >
            Créer mon espace vendeur →
          </button>
        </div>
      </div>

      {/* ── SECTION CONFIANCE ── */}
      <div className="land-section" style={{ background: "#0A0A0F", padding: "48px 32px" }}>
        <div style={{ display: "flex", gap: "20px", justifyContent: "center", flexWrap: "wrap" }}>
          {[
            { icon: "🛡️", label: "Sans pub" },
            { icon: "🔒", label: "Données sécurisées" },
            { icon: "🇫🇷", label: "Made in France" },
          ].map((badge, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px", background: "#13131F", borderRadius: "999px", padding: "12px 22px", border: "1px solid rgba(255,255,255,0.07)" }}>
              <span style={{ fontSize: "18px" }}>{badge.icon}</span>
              <span style={{ fontSize: "13px", fontWeight: 700, color: "rgba(255,255,255,0.5)", letterSpacing: "0.3px" }} dangerouslySetInnerHTML={{ __html: badge.label }} />
            </div>
          ))}
        </div>
      </div>

      {/* ── CTA FINAL ── */}
      <div style={{ background: "linear-gradient(135deg, #D41A6E 0%, #E87010 100%)", padding: "80px 32px", textAlign: "center" }}>
        <h2 style={{ fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 900, color: "white", margin: "0 0 14px", letterSpacing: "-1px" }}>Prêt à crusher ? 🚀</h2>
        <p style={{ fontSize: "17px", color: "rgba(255,255,255,0.8)", margin: "0 0 32px" }}>Clients, commerçants — CrushNow est fait pour vous deux.</p>
        <div style={{ display: "flex", gap: "16px", justifyContent: "center", flexWrap: "wrap" }}>
          <a
            href="https://play.google.com/store/apps/details?id=com.liabra.crushme"
            target="_blank"
            rel="noopener noreferrer"
            style={{ height: "56px", padding: "0 40px", background: "white", color: "#D41A6E", border: "none", borderRadius: "14px", fontSize: "16px", fontWeight: 800, cursor: "pointer", display: "inline-flex", alignItems: "center", textDecoration: "none", boxShadow: "0 8px 40px rgba(0,0,0,0.25)" }}
          >
            Télécharger l'app
          </a>
          <button
            onClick={() => navigate("/register")}
            style={{ height: "56px", padding: "0 40px", background: "rgba(255,255,255,0.15)", color: "white", border: "2px solid rgba(255,255,255,0.5)", borderRadius: "14px", fontSize: "16px", fontWeight: 700, cursor: "pointer" }}
          >
            Espace vendeur →
          </button>
        </div>
      </div>

      {/* ── FOOTER ── */}
      <div style={{ background: "#0A0A0F", padding: "28px 32px", textAlign: "center", borderTop: "1px solid #111827" }}>
        <p style={{ fontSize: "11px", color: "#334155", margin: "0 0 8px" }}>💘 CrushNow® — Marque enregistrée INPI n° 5152773</p>
        <div style={{ display: "flex", gap: "20px", justifyContent: "center", flexWrap: "wrap" }}>
          {[{ label: "CGU", path: "/cgu" }, { label: "Confidentialité", path: "/privacy" }, { label: "Mentions légales", path: "/legal" }, { label: "À propos", path: "/about" }].map((l, i) => (
            <Link key={i} to={l.path} style={{ fontSize: "11px", color: "#334155", textDecoration: "none" }}>{l.label}</Link>
          ))}
        </div>
        <p style={{ fontSize: "11px", color: "#1e293b", marginTop: "8px" }}>© 2026 Tous droits réservés</p>
      </div>
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  return isApp() ? <LandingApp navigate={navigate} /> : <LandingWeb navigate={navigate} />;
};

export default LandingPage;
